import { asString } from '../parser';
import type { Rule } from './types';

const ACTION_VERBS = [
  // creation
  'create', 'creates', 'generate', 'generates', 'build', 'builds', 'make', 'makes',
  'produce', 'produces', 'scaffold', 'scaffolds', 'compose', 'composes',
  'draft', 'drafts', 'author', 'authors', 'write', 'writes',
  // analysis / inspection
  'analyze', 'analyzes', 'analyse', 'analyses', 'audit', 'audits', 'review', 'reviews',
  'inspect', 'inspects', 'evaluate', 'evaluates', 'assess', 'assesses', 'check', 'checks',
  'detect', 'detects', 'diagnose', 'diagnoses', 'profile', 'profiles', 'measure', 'measures',
  // transformation
  'convert', 'converts', 'transform', 'transforms', 'translate', 'translates',
  'parse', 'parses', 'render', 'renders', 'format', 'formats', 'extract', 'extracts',
  'summarize', 'summarizes', 'reformat', 'reformats', 'rewrite', 'rewrites',
  // orchestration / workflow
  'manage', 'manages', 'orchestrate', 'orchestrates', 'coordinate', 'coordinates',
  'process', 'processes', 'handle', 'handles', 'automate', 'automates', 'run', 'runs',
  'execute', 'executes', 'invoke', 'invokes', 'trigger', 'triggers',
  // io / integration
  'fetch', 'fetches', 'retrieve', 'retrieves', 'send', 'sends', 'post', 'posts',
  'import', 'imports', 'export', 'exports', 'sync', 'syncs', 'push', 'pushes',
  'pull', 'pulls', 'upload', 'uploads', 'download', 'downloads',
  // verification / quality
  'validate', 'validates', 'verify', 'verifies', 'test', 'tests', 'lint', 'lints',
  'monitor', 'monitors', 'track', 'tracks',
  // guidance / instruction (Anthropic skills lean on these)
  'guide', 'guides', 'help', 'helps', 'apply', 'applies', 'enforce', 'enforces',
  'teach', 'teaches', 'walk', 'walks', 'support', 'supports', 'enable', 'enables',
  'provide', 'provides', 'package', 'packages', 'plan', 'plans',
  // domain operations
  'deploy', 'deploys', 'iterate', 'iterates', 'optimize', 'optimizes',
  'refactor', 'refactors', 'migrate', 'migrates', 'configure', 'configures',
  'setup', 'set', 'sets', 'install', 'installs', 'fix', 'fixes', 'debug', 'debugs',
  'ship', 'ships', 'release', 'releases',
];

// Noun-led description openers that are acceptable and common in well-rated skills.
// Example: "Toolkit for creating GIFs…", "Suite of tools for…", "Knowledge and utilities for…"
const NOUN_LED_OPENERS = [
  /^toolkit\b/i,
  /^suite\b/i,
  /^set\b/i,
  /^knowledge\b/i,
  /^utilities\b/i,
  /^a\s+(?:set|guide|toolkit|suite|collection|library)\b/i,
  /^guide\b/i,
  /^framework\b/i,
  /^use this skill\b/i,
  /^interactive guide\b/i,
];

const WHEN_PATTERNS = [
  /use when/i,
  /when (?:the )?user/i,
  /\basks? to\b/i,
  /\bmentions?\b/i,
  /\bsays?\b/i,
  /\bif (?:the )?user/i,
  /\bduring\b/i,
];

const VAGUE_OPENERS = [
  /^helps with/i,
  /^helps users?/i,
  /^assists? with/i,
  /^does things/i,
  /^a skill that/i,
  /^this skill/i,
];

const FIRST_PERSON = /\b(?:i (?:will|can|am)|we (?:will|can)|our team)\b/i;

export const descriptionRules: Rule[] = [
  {
    id: 'desc.present',
    category: 'description',
    severity: 'fail',
    weight: 8,
    title: '`description` field present',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description).trim();
      if (!desc) {
        return {
          status: 'fail',
          message: 'Missing `description` field in frontmatter.',
          fix: 'Add a `description` explaining what the skill does AND when to use it.',
        };
      }
      return { status: 'pass', message: `${desc.length} chars.` };
    },
  },
  {
    id: 'desc.under-1024',
    category: 'description',
    severity: 'fail',
    weight: 3,
    title: 'Description under 1024 characters',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description);
      if (!desc) return { status: 'skipped', message: 'No description.' };
      if (desc.length > 1024) {
        return {
          status: 'fail',
          message: `Description is ${desc.length} chars (max 1024).`,
          fix: 'Trim the description; move detail into the SKILL.md body or `references/`.',
        };
      }
      return { status: 'pass', message: `${desc.length} / 1024 chars.` };
    },
  },
  {
    id: 'desc.no-xml',
    category: 'description',
    severity: 'fail',
    weight: 2,
    title: 'No XML tags in description',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description);
      if (!desc) return { status: 'skipped', message: 'No description.' };
      if (/[<>]/.test(desc)) {
        return {
          status: 'fail',
          message: 'Description contains `<` or `>`, which is forbidden in frontmatter.',
          fix: 'Remove angle brackets. Frontmatter ends up in Claude\'s system prompt.',
        };
      }
      return { status: 'pass', message: 'No angle brackets.' };
    },
  },
  {
    id: 'desc.has-what',
    category: 'description',
    severity: 'warn',
    weight: 4,
    title: 'Describes what the skill does',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description);
      if (!desc) return { status: 'skipped', message: 'No description.' };
      const head = desc.slice(0, 180);
      const headLower = head.toLowerCase();
      const hasVerb = ACTION_VERBS.some((v) => new RegExp(`\\b${v}\\b`).test(headLower));
      const hasNounOpener = NOUN_LED_OPENERS.some((re) => re.test(head.trim()));
      if (!hasVerb && !hasNounOpener) {
        return {
          status: 'warn',
          message: 'No clear action verb or noun-led pattern in the opener.',
          fix: 'Start with what the skill does: "Creates X", "Analyzes Y", or "Toolkit for Z".',
        };
      }
      return { status: 'pass', message: hasVerb ? 'Action verb found.' : 'Noun-led pattern found.' };
    },
  },
  {
    id: 'desc.has-when',
    category: 'description',
    severity: 'warn',
    weight: 6,
    title: 'Includes trigger conditions ("when to use")',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description);
      if (!desc) return { status: 'skipped', message: 'No description.' };
      const matched = WHEN_PATTERNS.some((p) => p.test(desc));
      if (!matched) {
        return {
          status: 'warn',
          message: 'No trigger condition found ("Use when…", "when user asks to…").',
          fix: 'Tell Claude when to load this skill. This is the #1 cause of under-triggering.',
        };
      }
      return { status: 'pass', message: 'Trigger condition present.' };
    },
  },
  {
    id: 'desc.has-trigger-phrases',
    category: 'description',
    severity: 'info',
    weight: 2,
    title: 'Lists concrete user trigger phrases',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description);
      if (!desc) return { status: 'skipped', message: 'No description.' };
      const signals = [
        /['"][^'"]{3,}['"]/,
        /\basks? to\b/i,
        /\bmentions?\b/i,
        /\bsays?\b/i,
        /\brequests?\b/i,
        /\bwants? to\b/i,
        /\bsuch as\b/i,
        /\be\.g\.?\b/i,
        /\bincluding\b/i,
        /\bfor (?:example|instance)\b/i,
      ];
      if (signals.some((re) => re.test(desc))) {
        return { status: 'pass', message: 'Concrete trigger phrasing present.' };
      }
      return {
        status: 'warn',
        message: 'No concrete user phrasings (quoted strings, "such as", "e.g.").',
        fix: 'Quote 2 or 3 things the user might literally say. Improves matching.',
      };
    },
  },
  {
    id: 'desc.not-vague',
    category: 'description',
    severity: 'warn',
    weight: 3,
    title: 'Avoids vague openers',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description).trim();
      if (!desc) return { status: 'skipped', message: 'No description.' };
      const tooShort = desc.length < 40;
      const matched = VAGUE_OPENERS.find((p) => p.test(desc));
      if (matched || tooShort) {
        return {
          status: 'warn',
          message: matched
            ? `Description opens with a vague phrase ("${matched.source.replace(/[^a-z ]/gi, '')}").`
            : 'Description is too short to be useful.',
          fix: 'Be specific: name the inputs, outputs, and triggering tasks.',
        };
      }
      return { status: 'pass', message: 'Specific opener.' };
    },
  },
  {
    id: 'desc.no-first-person',
    category: 'description',
    severity: 'info',
    weight: 1,
    title: 'Uses third-person voice',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description);
      if (!desc) return { status: 'skipped', message: 'No description.' };
      if (FIRST_PERSON.test(desc)) {
        return {
          status: 'warn',
          message: 'Description uses first-person voice ("I will…", "we…").',
          fix: 'Describe the skill in third person. Claude reads this to decide when to load it.',
        };
      }
      return { status: 'pass', message: 'Third-person voice.' };
    },
  },
  {
    id: 'desc.length-sweet-spot',
    category: 'description',
    severity: 'info',
    weight: 1,
    title: 'Description length in sweet spot',
    appliesTo: 'both',
    run(ctx) {
      const desc = asString(ctx.parsed.frontmatter.description);
      if (!desc) return { status: 'skipped', message: 'No description.' };
      if (desc.length < 120) return { status: 'warn', message: 'Under 120 chars. Likely too thin.' };
      if (desc.length > 800) return { status: 'warn', message: 'Over 800 chars. Consider trimming.' };
      return { status: 'pass', message: `${desc.length} chars (120–800 ideal).` };
    },
  },
];
